import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PaletteDndProvider } from '@miroculus/nucleo';
import {
  AccountSettings,
  ConnectedLogin,
  ConnectedLogout,
  ConnectedSignUp,
  ProtocolSelection,
  ProtocolCanvas,
  ProtocolCreation,
  DeviceDetails,
  Devices,
  Anaconda,
  ProjectSelection,
  Members,
  ProtocolRouting,
  ForgotPassword,
  ResetPassword,
  RecentProtocols,
  RunDetails,
  Drafts,
  CreateOrganization,
  Organization,
  OrganizationSettings,
  OrganizationInvite,
} from 'containers';
import { ProtocolLoader, PageNotFoundError } from 'components';
import {
  SendProtocolModal,
  MemberModal,
  OrganizationMemberModal,
  TeamModal,
  DeleteTeamModal,
} from 'modals';
import {
  ACCOUNT_SETTINGS_URL,
  LOGIN_URL,
  SIGNUP_URL,
  LOGOUT_URL,
  DASHBOARD_URL,
  ANACONDA_URL,
  PROJECT_SELECTION_URL,
  MEMBERS_URL,
  NEW_MEMBER_URL,
  FORGOT_PASSWORD_URL,
  RESET_PASSWORD_URL,
  RECENT_URL,
  DRAFTS_URL,
  CREATE_ORGANIZATION_URL,
  ORGANIZATION_INVITE_URL,
  organizationUrl,
  organizationSettingsUrl,
  instrumentsUrl,
  instrumentDetailsUrl,
  instrumentRunDetailsUrl,
  editProtocolUrl,
  protocolUrl,
  saveProtocolUrl,
  teamUrl,
  teamUrlWithVisibility,
  protocolRouteUrl,
  copyProtocolUrl,
  sendProtocolUrl,
  editMemberUrl,
  newOrganizationMemberUrl,
  editOrganizationMemberUrl,
  newOrganizationTeamUrl,
  editOrganizationTeamUrl,
  deleteOrganizationTeamUrl,
} from 'cons/routes';
import AutoLogin from './AutoLogin';
import OrganizationLoader from './OrganizationLoader';
import LayoutRoute from './LayoutRoute';
import PrivateRoute from './PrivateRoute';
import ValidatorRoute from './ValidatorRoute';
import { getLogged } from 'reduxModules/auth/selectors';
import { currentDeviceSelector } from 'reduxModules/devices/selectors';
import { getCurrentWorkspace } from 'reduxModules/workspaces/selectors';
import { canEditOrganization } from 'reduxModules/organizations/selectors';

// Helper component to handle redirects with params
const TeamRedirect = () => {
  const { teamId } = useParams();
  return <Navigate to={teamUrlWithVisibility(teamId, 'templates')} replace />;
};

const AppRoutes = () => {
  return (
    <AutoLogin>
      <OrganizationLoader>
        <Routes>
          <Route path={LOGIN_URL} element={<ConnectedLogin />} />
          <Route path={SIGNUP_URL} element={<ConnectedSignUp />} />
          <Route path={ORGANIZATION_INVITE_URL} element={<OrganizationInvite />} />
          <Route path={FORGOT_PASSWORD_URL} element={<ForgotPassword />} />
          <Route path={RESET_PASSWORD_URL} element={<ResetPassword />} />
          <Route
            path={CREATE_ORGANIZATION_URL}
            element={
              <PrivateRoute signUp redirect>
                <CreateOrganization />
              </PrivateRoute>
            }
          />
          <Route
            path={LOGOUT_URL}
            element={
              <PrivateRoute>
                <ConnectedLogout />
              </PrivateRoute>
            }
          />
          <Route
            path={DASHBOARD_URL}
            element={
              <LayoutRoute>
                <RecentProtocols />
              </LayoutRoute>
            }
          />
          <Route
            path={DRAFTS_URL}
            element={
              <LayoutRoute>
                <Drafts />
              </LayoutRoute>
            }
          />
          <Route
            path={RECENT_URL}
            element={
              <LayoutRoute>
                <RecentProtocols />
              </LayoutRoute>
            }
          />
          <Route
            path={ACCOUNT_SETTINGS_URL}
            element={
              <LayoutRoute>
                <AccountSettings />
              </LayoutRoute>
            }
          />
          <Route
            path={sendProtocolUrl(':teamId', ':id')}
            element={
              <LayoutRoute>
                <SendProtocolModal />
              </LayoutRoute>
            }
          />
          <Route
            path={saveProtocolUrl(':teamId', ':id')}
            element={
              <LayoutRoute>
                <ProtocolLoader>
                  <ProtocolCreation />
                </ProtocolLoader>
              </LayoutRoute>
            }
          />
          <Route
            path={copyProtocolUrl(':teamId', ':id')}
            element={
              <LayoutRoute>
                <ProtocolLoader copy>
                  <ProtocolCreation />
                </ProtocolLoader>
              </LayoutRoute>
            }
          />
          <Route
            path={editProtocolUrl(':teamId', ':id')}
            element={
              <LayoutRoute>
                <ProtocolLoader>
                  <PaletteDndProvider>
                    <ProtocolCanvas editing />
                  </PaletteDndProvider>
                </ProtocolLoader>
              </LayoutRoute>
            }
          />
          <Route
            path={protocolRouteUrl(':teamId', ':id')}
            element={
              <LayoutRoute>
                <ProtocolLoader solution>
                  <ProtocolRouting />
                </ProtocolLoader>
              </LayoutRoute>
            }
          />
          <Route
            path={protocolUrl(':teamId', ':id')}
            element={
              <LayoutRoute>
                <ProtocolLoader>
                  <ProtocolCanvas />
                </ProtocolLoader>
              </LayoutRoute>
            }
          />
          <Route
            path={organizationUrl(':orgSlug')}
            element={
              <LayoutRoute>
                <Organization />
              </LayoutRoute>
            }
          />
          {/* Organization member routes */}
          <Route
            path={newOrganizationMemberUrl(':orgSlug')}
            element={
              <LayoutRoute isPrivate={false} validators={[getLogged, canEditOrganization]} redirectionPath={DASHBOARD_URL}>
                <OrganizationSettings initialTab={1} />
                <OrganizationMemberModal />
              </LayoutRoute>
            }
          />
          <Route
            path={editOrganizationMemberUrl(':orgSlug', ':id')}
            element={
              <LayoutRoute isPrivate={false} validators={[getLogged, canEditOrganization]} redirectionPath={DASHBOARD_URL}>
                <OrganizationSettings initialTab={1} />
                <OrganizationMemberModal />
              </LayoutRoute>
            }
          />
          {/* Organization team routes */}
          <Route
            path={newOrganizationTeamUrl(':orgSlug')}
            element={
              <LayoutRoute isPrivate={false} validators={[getLogged, canEditOrganization]} redirectionPath={DASHBOARD_URL}>
                <OrganizationSettings initialTab={2} />
                <TeamModal />
              </LayoutRoute>
            }
          />
          <Route
            path={editOrganizationTeamUrl(':orgSlug', ':id')}
            element={
              <LayoutRoute isPrivate={false} validators={[getLogged, canEditOrganization]} redirectionPath={DASHBOARD_URL}>
                <OrganizationSettings initialTab={2} />
                <TeamModal />
              </LayoutRoute>
            }
          />
          <Route
            path={deleteOrganizationTeamUrl(':orgSlug', ':id')}
            element={
              <LayoutRoute isPrivate={false} validators={[getLogged, canEditOrganization]} redirectionPath={DASHBOARD_URL}>
                <OrganizationSettings initialTab={2} />
                <DeleteTeamModal />
              </LayoutRoute>
            }
          />
          <Route
            path={organizationSettingsUrl(':orgSlug')}
            element={
              <LayoutRoute isPrivate={false} validators={[getLogged, canEditOrganization]} redirectionPath={DASHBOARD_URL}>
                <OrganizationSettings />
              </LayoutRoute>
            }
          />
          {/* Team redirect */}
          <Route path={teamUrl(':teamId')} element={<TeamRedirect />} />
          <Route
            path={teamUrlWithVisibility(':teamId', ':visibility')}
            element={
              <LayoutRoute>
                <ProtocolSelection />
              </LayoutRoute>
            }
          />
          <Route
            path={instrumentsUrl(':orgSlug')}
            element={
              <LayoutRoute>
                <Devices />
              </LayoutRoute>
            }
          />
          <Route
            path={instrumentDetailsUrl(':orgSlug', ':deviceId')}
            element={
              <LayoutRoute>
                <DeviceDetails />
              </LayoutRoute>
            }
          />
          <Route
            path={instrumentRunDetailsUrl(':orgSlug', ':deviceId', ':runId')}
            element={
              <LayoutRoute>
                <RunDetails />
              </LayoutRoute>
            }
          />
          {/* Member routes */}
          <Route
            path={NEW_MEMBER_URL}
            element={
              <LayoutRoute isPrivate={false} validators={[getCurrentWorkspace]}>
                <Members />
                <MemberModal />
              </LayoutRoute>
            }
          />
          <Route
            path={editMemberUrl(':id')}
            element={
              <LayoutRoute isPrivate={false} validators={[getCurrentWorkspace]}>
                <Members />
                <MemberModal />
              </LayoutRoute>
            }
          />
          <Route
            path={MEMBERS_URL}
            element={
              <LayoutRoute isPrivate={false} validators={[getCurrentWorkspace]}>
                <Members />
              </LayoutRoute>
            }
          />
          <Route
            path={PROJECT_SELECTION_URL}
            element={
              <LayoutRoute isPrivate={false} validators={[currentDeviceSelector]}>
                <ProjectSelection />
              </LayoutRoute>
            }
          />
          <Route
            path={ANACONDA_URL}
            element={
              <LayoutRoute isPrivate={false} validators={[currentDeviceSelector]}>
                <Anaconda />
              </LayoutRoute>
            }
          />
          <Route path="*" element={<PageNotFoundError />} />
        </Routes>
      </OrganizationLoader>
    </AutoLogin>
  );
};

export default AppRoutes;
